import React, { useMemo, useState, forwardRef, useImperativeHandle } from 'react';
import { parseDesc, TODAS_LAS_UNIDADES } from '../hooks/useEventos';
import { exportarIndicadoresPDFVectorial } from '../utils/exportPDFVector';
import '../styles/global.css';
import { normISO } from '../utils/surgeryHelpers';

/* ── Helpers ──────────────────────────────────────────────── */
const getMin   = (doc) => { const d = new Date(normISO(doc?.event?.start?.dateTime)); return isNaN(d) ? null : d.getHours()*60+d.getMinutes(); };
const getDurMin= (doc) => {
  const s = new Date(normISO(doc?.event?.start?.dateTime));
  const e = new Date(normISO(doc?.event?.end?.dateTime));
  if (isNaN(s)||isNaN(e)) return 0;
  return Math.max(0, (e-s)/60000);
};
const fmtH = (min) => {
  const h = Math.floor(min/60), m = min%60;
  return h > 0 ? `${h}h ${m>0?m+'min':''}` : `${m}min`;
};
const getSummary = (doc) => (doc?.event?.summary||'').toLowerCase();

/* Normaliza texto para agrupar/contar correctamente en los indicadores:
   quita espacios al inicio/fin y pasa a mayúsculas, para que variaciones
   como " Dr. Juan Pérez" / "dr. juan pérez" / "Dr. Juan Pérez " no se
   cuenten como valores distintos y rompan los totales. */
const normalizarTexto = (v) => (v || '').trim().toUpperCase();

const getField   = (doc, key) => {
  const f = parseDesc(doc?.event?.description);
  return normalizarTexto(f[key]);
};

/* Frecuencia: [{ label, count }] ordenado desc */
const frecuencia = (docs, fn, max=10) => {
  const map = {};
  docs.forEach(d => {
    const k = fn(d)||'Sin datos';
    if (k==='Sin datos'||k===''||k==='NA') return;
    map[k] = (map[k]||0)+1;
  });
  return Object.entries(map)
    .sort((a,b)=>b[1]-a[1])
    .slice(0,max)
    .map(([label,count])=>({label,count}));
};

/* Paleta teal */
const TEAL = '#04afaa';
const COLORS_STATUS = {
  pending   : '#04afaa',
  confirmada: '#d97706',
  cancelada : '#e12f3b',
  realizado : '#6b7280',
};

/* ── Componentes base ─────────────────────────────────────── */
const KpiCard = ({ icon, label, value, sub, color='#04afaa' }) => (
  <div style={{ background:'var(--color-background-secondary)', borderRadius:'var(--border-radius-md)', padding:'1rem', display:'flex', flexDirection:'column', gap:'6px' }}>
    <div style={{ fontSize:'20px', color }}>{icon}</div>
    <div style={{ fontSize:'13px', color:'var(--color-text-secondary)' }}>{label}</div>
    <div style={{ fontSize:'24px', fontWeight:500, color:'var(--color-text-primary)', lineHeight:1 }}>{value}</div>
    {sub && <div style={{ fontSize:'12px', color:'var(--color-text-secondary)' }}>{sub}</div>}
  </div>
);

const SectionTitle = ({ icon, title }) => (
  <div style={{ display:'flex', alignItems:'center', gap:'8px', margin:'1.5rem 0 .75rem', borderBottom:'0.5px solid var(--color-border-tertiary)', paddingBottom:'8px' }}>
    <i className={`ti ti-${icon}`} style={{ fontSize:'16px', color:'var(--color-text-secondary)' }} aria-hidden="true"/>
    <span style={{ fontSize:'14px', fontWeight:500, color:'var(--color-text-primary)' }}>{title}</span>
  </div>
);

/* Barra horizontal — value numérico para calcular %, label para mostrar */
const BarRow = ({ label, value, displayValue, max, color=TEAL }) => (
  <div style={{ marginBottom:'8px' }}>
    <div style={{ display:'flex', justifyContent:'space-between', fontSize:'12px', marginBottom:'3px' }}>
      <span style={{ color:'var(--color-text-primary)', fontWeight:500, overflow:'hidden', textOverflow:'ellipsis', whiteSpace:'nowrap', maxWidth:'70%' }}>{label}</span>
      <span style={{ color:'var(--color-text-secondary)', flexShrink:0, marginLeft:'8px' }}>{displayValue !== undefined ? displayValue : value}</span>
    </div>
    <div style={{ background:'var(--color-background-secondary)', borderRadius:'4px', height:'8px', overflow:'hidden' }}>
      <div style={{ background:color, borderRadius:'4px', height:'100%', width:`${max > 0 ? Math.round((value/max)*100) : 0}%`, transition:'width .4s' }}/>
    </div>
  </div>
);

/* Dona simple con SVG */
const DonaChart = ({ segments }) => {
  const total = segments.reduce((a,s)=>a+s.value,0);
  if (!total) return <p style={{fontSize:'13px',color:'var(--color-text-secondary)'}}>Sin datos</p>;
  let offset = 0;
  const R=60, cx=70, cy=70, stroke=22, circ=2*Math.PI*R;
  return (
    <div style={{ display:'flex', alignItems:'center', gap:'20px', flexWrap:'wrap' }}>
      <svg width="140" height="140" viewBox="0 0 140 140" role="img" aria-label="Gráfica de distribución por estado">
        {segments.map((s,i)=>{
          const pct = s.value/total;
          const dash = pct*circ;
          const el = (
            <circle key={i} cx={cx} cy={cy} r={R}
              fill="none" stroke={s.color} strokeWidth={stroke}
              strokeDasharray={`${dash} ${circ-dash}`}
              strokeDashoffset={-offset*circ}
              style={{ transition:'stroke-dasharray .4s' }}
            />
          );
          offset += pct;
          return el;
        })}
        <text x={cx} y={cy-4} textAnchor="middle" fontSize="20" fontWeight="500" fill="var(--color-text-primary)">{total}</text>
        <text x={cx} y={cy+14} textAnchor="middle" fontSize="11" fill="var(--color-text-secondary)">total</text>
      </svg>
      <div style={{ display:'flex', flexDirection:'column', gap:'8px' }}>
        {segments.map((s,i)=>(
          <div key={i} style={{ display:'flex', alignItems:'center', gap:'8px' }}>
            <div style={{ width:'10px',height:'10px',borderRadius:'2px',background:s.color,flexShrink:0 }}/>
            <span style={{ fontSize:'12px', color:'var(--color-text-secondary)' }}>
              {s.label} — <strong style={{ color:'var(--color-text-primary)',fontWeight:500 }}>{s.value}</strong>
              <span style={{ color:'var(--color-text-secondary)' }}> ({Math.round(s.value/total*100)}%)</span>
            </span>
          </div>
        ))}
      </div>
    </div>
  );
};

/* Bucketiza los docs por día dentro del rango — usado tanto por
   TendenciaDiaria (pantalla) como por la exportación a PDF vectorial */
const calcularDias = (docs, desde, hasta) => {
  if (!desde || !hasta) return [];
  const map = {};
  const d0 = new Date(desde + 'T00:00'), d1 = new Date(hasta + 'T23:59');
  for (let d = new Date(d0); d <= d1; d.setDate(d.getDate() + 1)) {
    // Fecha local (toISOString usa UTC y recorría los días por la noche)
    map[`${d.getFullYear()}-${String(d.getMonth() + 1).padStart(2, '0')}-${String(d.getDate()).padStart(2, '0')}`] = 0;
  }
  docs.forEach((doc) => {
    const iso = normISO(doc?.event?.start?.dateTime);
    if (!iso) return;
    const fecha = iso.split('T')[0];
    if (fecha in map) map[fecha]++;
  });
  return Object.entries(map).map(([fecha, count]) => ({
    fecha,
    label: new Date(fecha + 'T00:00').toLocaleDateString('es-MX', { day: '2-digit', month: 'short' }),
    count,
  }));
};

/* Tendencia diaria */
const TendenciaDiaria = ({ docs, desde, hasta }) => {
  const dias = useMemo(() => calcularDias(docs, desde, hasta), [docs, desde, hasta]);

  const max = Math.max(...dias.map(d=>d.count),1);
  const chartH = 120;

  if (!dias.length) return <p style={{fontSize:'13px',color:'var(--color-text-secondary)'}}>Selecciona un rango de fechas</p>;

  return (
    <div style={{ overflowX:'auto' }}>
      <div style={{ display:'flex', alignItems:'flex-end', gap:'4px', minWidth:dias.length*36+'px', height:chartH+32+'px', paddingBottom:'24px', position:'relative' }}>
        {dias.map(({fecha,label,count})=>(
          <div key={fecha} style={{ display:'flex', flexDirection:'column', alignItems:'center', flex:'0 0 32px' }}>
            {count>0&&<span style={{ fontSize:'10px', color:'var(--color-text-secondary)', marginBottom:'2px' }}>{count}</span>}
            <div title={`${label}: ${count}`} style={{
              background: count>0 ? TEAL : 'var(--color-background-secondary)',
              borderRadius:'3px 3px 0 0',
              width:'28px',
              height: Math.max((count/max)*chartH, count>0?6:2)+'px',
              transition:'height .3s',
            }}/>
            <span style={{ fontSize:'9px', color:'var(--color-text-secondary)', marginTop:'4px', writingMode:'vertical-rl', transform:'rotate(180deg)', whiteSpace:'nowrap', maxHeight:'40px' }}>{label}</span>
          </div>
        ))}
      </div>
    </div>
  );
};

/* ── Dashboard principal ─────────────────────────────────── */
const DashboardPage = forwardRef(({ todos, calendar, rangoDesde, rangoHasta, uhList }, ref) => {
  const [tab, setTab] = useState('sala');

  // Unidad que se excluye de los indicadores cuando se ve la vista
  // agregada "Todas las unidades" (perfil admin/soporte de Corporativo)
  // — no representa actividad quirúrgica real. Se resuelve su calendar
  // real desde uhList en vez de comparar el texto a ciegas.
  const NOMBRE_UNIDAD_EXCLUIDA = 'MAC Integración Procesos Clínicos';
  const calendarExcluido = uhList?.find(
    (u) => normalizarTexto(u.hospital) === normalizarTexto(NOMBRE_UNIDAD_EXCLUIDA)
  )?.calendar;

  /* Filtrar por hospital y rango de fechas */
  const docs = useMemo(()=>{
    const desde = rangoDesde ? new Date(rangoDesde) : null;
    const hasta  = rangoHasta ? new Date(rangoHasta+'T23:59') : null;
    return todos.filter(doc=>{
      const f = parseDesc(doc?.event?.description);
      const uh = f['Unidad Hospitalaroa']||f['Unidad Hospitalaria']||'';
      if (calendar !== TODAS_LAS_UNIDADES && uh !== calendar) return false;
      // En la vista agregada, se excluye la unidad no-clínica
      if (calendar === TODAS_LAS_UNIDADES && calendarExcluido && uh === calendarExcluido) return false;
      const iso = normISO(doc?.event?.start?.dateTime);
      if (!iso) return false;
      const inicio = new Date(iso);
      return (!desde||inicio>=desde) && (!hasta||inicio<=hasta);
    });
  },[todos,calendar,rangoDesde,rangoHasta,calendarExcluido]);

  /* ── KPIs globales ──────────────────────────────────────── */
  const total      = docs.length;
  const pendientes = docs.filter(d=>getSummary(d)==='pending').length;
  const confirmadas= docs.filter(d=>getSummary(d)==='confirmada').length;
  const canceladas = docs.filter(d=>getSummary(d)==='cancelada').length;
  // Realizadas = cirugías marcadas con estatus "Realizada" (antes se contaban
  // pendientes/confirmadas con hora de fin vencida y no se mostraban en pantalla)
  const realizadas = docs.filter(d=>getSummary(d)==='realizada').length;
  const tasaCancelacion = total>0 ? Math.round((canceladas/total)*100) : 0;

  /* Reagendados: cirugías con al menos un motivo de reagendamiento
     capturado (se guarda solo el más reciente, ver SurgeryEditModal.js) */
  const reagendados = docs.filter(d => Boolean(d?.event?.motivoReagendamiento)).length;

  const durTotalMin = docs.reduce((a,d)=>a+getDurMin(d),0);
  const durPromMin  = total>0 ? Math.round(durTotalMin/total) : 0;

  /* ── Datos por dimensión ────────────────────────────────── */
  const porSala     = frecuencia(docs, d=>getField(d,'AppointmentType'));
  const porMedico   = frecuencia(docs, d=>getField(d,'Medico'));
  const porProc     = frecuencia(docs, d=>getField(d,'Procedimiento'));
  const porAnest    = frecuencia(docs, d=>getField(d,'Anestesiologo'));
  const porCliente  = frecuencia(docs, d=>getField(d,'TipoCliente'));

  /* Motivos de cancelación — solo sobre las cirugías canceladas */
  const docsCancelados     = docs.filter(d=>getSummary(d)==='cancelada');
  const porMotivoCancelacion = frecuencia(docsCancelados, d=>normalizarTexto(d?.event?.motivoCancelacion));
  const maxMotivoCancelacion = Math.max(...porMotivoCancelacion.map(m=>m.count),1);
  const sinMotivoCancelacion = docsCancelados.length - porMotivoCancelacion.reduce((a,m)=>a+m.count,0);

  /* Motivos de reagendamiento — solo sobre las cirugías reagendadas */
  const docsReagendados      = docs.filter(d => Boolean(d?.event?.motivoReagendamiento));
  const porMotivoReagendamiento = frecuencia(docsReagendados, d=>normalizarTexto(d?.event?.motivoReagendamiento));
  const maxMotivoReagendamiento = Math.max(...porMotivoReagendamiento.map(m=>m.count),1);

  /* Horas ocupadas por sala */
  const horasPorSala = useMemo(()=>{
    const map = {};
    docs.forEach(d=>{
      const sala = getField(d,'AppointmentType')||'Sin sala';
      map[sala] = (map[sala]||0)+getDurMin(d);
    });
    return Object.entries(map).sort((a,b)=>b[1]-a[1]).slice(0,8).map(([label,min])=>({label,count:Math.round(min)}));
  },[docs]);
  const maxHoras = Math.max(...horasPorSala.map(h=>h.count),1);

  /* Requerimientos especiales */
  const checkReq = (v)=>{
    if (!v) return false;
    if (Array.isArray(v)) return v.length>0;
    if (typeof v==='object') return Object.values(v).some(Boolean);
    return Boolean(v);
  };
  const reqImg  = docs.filter(d=>checkReq(d?.event?.imagenologia)).length;
  const reqMat  = docs.filter(d=>checkReq(d?.event?.materialEspecial)).length;
  const reqRent = docs.filter(d=>checkReq(d?.event?.rentaEquipos)).length;
  const reqMue  = docs.filter(d=>checkReq(d?.event?.muestrapatologica)).length;

  /* Segmentos dona */
  const donaEstado = [
    { label:'Pendiente',  value:pendientes,  color:COLORS_STATUS.pending    },
    { label:'Confirmada', value:confirmadas, color:COLORS_STATUS.confirmada  },
    { label:'Realizada',  value:realizadas,  color:COLORS_STATUS.realizado   },
    { label:'Cancelada',  value:canceladas,  color:COLORS_STATUS.cancelada   },
  ].filter(s=>s.value>0);

  const donaTipoCliente = porCliente.map((p,i)=>({
    label:p.label, value:p.count,
    color:['#04afaa','#0582a9','#85c100','#d97706','#6b7280'][i%5]
  }));

  const donaMotivoCancelacion = porMotivoCancelacion.map((p,i)=>({
    label:p.label, value:p.count,
    color:['#e12f3b','#d97706','#0582a9','#85c100','#7c3aed','#6b7280'][i%6]
  }));

  const donaMotivoReagendamiento = porMotivoReagendamiento.map((p,i)=>({
    label:p.label, value:p.count,
    color:['#0582a9','#04afaa','#d97706','#85c100','#7c3aed','#6b7280'][i%6]
  }));

  /* ── Clasificación por unidad y estatus ──────────────────────
     Solo aplica en la vista "Todas las unidades" (perfil admin/soporte
     de Corporativo) — permite identificar actividad por hospital cuando
     los datos de varias unidades están mezclados en pantalla. */
  const esVistaTodas = calendar === TODAS_LAS_UNIDADES;
  const porUnidadEstatus = useMemo(() => {
    if (!esVistaTodas) return [];
    const map = {};
    docs.forEach((d) => {
      const raw = getField(d, 'Unidad Hospitalaroa') || getField(d, 'Unidad Hospitalaria');
      const unidad = raw || 'SIN UNIDAD';
      if (!map[unidad]) map[unidad] = { pendiente: 0, confirmada: 0, realizada: 0, cancelada: 0 };
      const s = getSummary(d);
      if (s === 'pending')    map[unidad].pendiente++;
      else if (s === 'confirmada') map[unidad].confirmada++;
      else if (s === 'realizada')  map[unidad].realizada++;
      else if (s === 'cancelada')  map[unidad].cancelada++;
    });
    return Object.entries(map)
      .map(([unidad, c]) => ({
        unidad,
        nombre: uhList?.find((u) => normalizarTexto(u.calendar) === unidad)?.hospital || unidad,
        ...c,
        total: c.pendiente + c.confirmada + c.realizada + c.cancelada,
      }))
      .sort((a, b) => b.total - a.total);
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [docs, esVistaTodas]);

  /* Tabs de ranking */
  const TABS = [
    { id:'sala',    label:'Por sala'         },
    { id:'medico',  label:'Por médico'       },
    { id:'proc',    label:'Por procedimiento'},
    { id:'anest',   label:'Por anestesiólogo'},
  ];
  const rankingData = { sala:porSala, medico:porMedico, proc:porProc, anest:porAnest };
  const maxRanking  = Math.max(...(rankingData[tab]||[]).map(r=>r.count),1);

  /* Tendencia diaria — misma bucketización que usa <TendenciaDiaria> en pantalla */
  const dias = useMemo(() => calcularDias(docs, rangoDesde, rangoHasta), [docs, rangoDesde, rangoHasta]);

  /* Expone exportarPDF() para que AgendaPage.js dispare la exportación
     vectorial reutilizando exactamente los mismos datos que se ven en
     pantalla (evita recalcular/duplicar toda esta lógica en otro lado). */
  useImperativeHandle(ref, () => ({
    exportarPDF: (opciones = {}) => exportarIndicadoresPDFVectorial({
      titulo: 'Indicadores · Agenda de Quirófano',
      hospital: opciones.hospital || '',
      rangoDesde, rangoHasta,
      nombreArchivo: opciones.nombreArchivo || `indicadores_${calendar}_${rangoDesde}_${rangoHasta}`,
      total, pendientes, confirmadas, canceladas, realizadas, tasaCancelacion,
      reagendados,
      dias,
      donaEstado, donaTipoCliente,
      rankingSala: porSala, rankingMedico: porMedico, rankingProc: porProc, rankingAnest: porAnest,
      horasPorSala,
      reqImg, reqMat, reqRent, reqMue,
      porMotivoCancelacion, donaMotivoCancelacion, sinMotivoCancelacion,
      porMotivoReagendamiento, donaMotivoReagendamiento,
      esVistaTodas, porUnidadEstatus,
    }),
  }), [
    docs, rangoDesde, rangoHasta, calendar, dias,
    total, pendientes, confirmadas, canceladas, realizadas, tasaCancelacion, reagendados,
    donaEstado, donaTipoCliente, porSala, porMedico, porProc, porAnest, horasPorSala,
    reqImg, reqMat, reqRent, reqMue, porMotivoCancelacion, donaMotivoCancelacion, sinMotivoCancelacion,
    porMotivoReagendamiento, donaMotivoReagendamiento,
    esVistaTodas, porUnidadEstatus,
  ]);

  /* ── Render ─────────────────────────────────────────────── */
  return (
    <div style={{ padding:'0 0 40px' }}>

      {/* Sin datos */}
      {total===0 && (
        <div style={{ alignItems:'center', display:'flex', flexDirection:'column', gap:'8px', padding:'60px 0', textAlign:'center' }}>
          <i className="ti ti-chart-bar" style={{ fontSize:'40px', color:'var(--color-text-secondary)' }} aria-hidden="true"/>
          <span style={{ fontSize:'15px', fontWeight:500, color:'var(--color-text-primary)' }}>Sin datos en el rango seleccionado</span>
          <span style={{ fontSize:'13px', color:'var(--color-text-secondary)' }}>Ajusta las fechas para ver indicadores</span>
        </div>
      )}

      {total>0 && <>

        {/* ── KPIs ─────────────────────────────────────────── */}
        <SectionTitle icon="chart-bar" title="Resumen general" />
        <div style={{ display:'grid', gridTemplateColumns:'repeat(auto-fit,minmax(140px,1fr))', gap:'12px' }}>
          <KpiCard icon={<i className="ti ti-calendar-event" aria-hidden="true"/>} label="Total cirugías" value={total} />
          <KpiCard icon={<i className="ti ti-clock-hour-4" aria-hidden="true"/>}   label="Pendientes"    value={pendientes}  color='#04afaa'/>
          <KpiCard icon={<i className="ti ti-circle-check" aria-hidden="true"/>}   label="Confirmadas"   value={confirmadas} color='#d97706'/>
          <KpiCard icon={<i className="ti ti-checks" aria-hidden="true"/>}         label="Realizadas"    value={realizadas}  color='#6b7280'/>
          <KpiCard icon={<i className="ti ti-circle-x" aria-hidden="true"/>}       label="Canceladas"    value={canceladas}  color='#e12f3b'/>
          <KpiCard icon={<i className="ti ti-calendar-repeat" aria-hidden="true"/>} label="Reagendados"  value={reagendados} color='#0582a9'/>
          <KpiCard icon={<i className="ti ti-alert-triangle" aria-hidden="true"/>} label="Tasa cancelación" value={`${tasaCancelacion}%`} color={tasaCancelacion>20?'#e12f3b':'#85c100'}/>
        </div>

        {/* ── Estado + Tipo de cliente ─────────────────────── */}
        <div style={{ display:'grid', gridTemplateColumns:'repeat(auto-fit,minmax(280px,1fr))', gap:'20px', marginTop:'8px' }}>
          <div>
            <SectionTitle icon="chart-pie" title="Distribución por estado" />
            <DonaChart segments={donaEstado}/>
          </div>
          <div>
            <SectionTitle icon="users" title="Tipo de cliente" />
            <DonaChart segments={donaTipoCliente}/>
          </div>
        </div>

        {/* ── Clasificación por unidad (solo vista "Todas las unidades") ── */}
        {esVistaTodas && porUnidadEstatus.length > 0 && (
          <>
            <SectionTitle icon="building-hospital" title="Clasificación por unidad" />
            <div style={{ overflowX: 'auto' }}>
              <table style={{ borderCollapse: 'collapse', fontSize: '12px', width: '100%' }}>
                <thead>
                  <tr>
                    {['Unidad', 'Pendientes', 'Confirmadas', 'Realizadas', 'Canceladas', 'Total'].map((h, i) => (
                      <th key={h} style={{
                        borderBottom: '1px solid var(--color-border-secondary)', color: 'var(--color-text-secondary)',
                        fontSize: '10px', fontWeight: 700, letterSpacing: '.4px', padding: '8px 10px',
                        textAlign: i === 0 ? 'left' : 'center', textTransform: 'uppercase', whiteSpace: 'nowrap',
                      }}>
                        {h}
                      </th>
                    ))}
                  </tr>
                </thead>
                <tbody>
                  {porUnidadEstatus.map((u) => (
                    <tr key={u.unidad}>
                      <td style={{ borderBottom: '1px solid var(--color-border-tertiary)', color: 'var(--color-text-primary)', fontWeight: 700, padding: '8px 10px', whiteSpace: 'nowrap' }}>
                        🏥 {u.nombre}
                      </td>
                      <td style={{ borderBottom: '1px solid var(--color-border-tertiary)', color: '#038f8a', padding: '8px 10px', textAlign: 'center' }}>{u.pendiente}</td>
                      <td style={{ borderBottom: '1px solid var(--color-border-tertiary)', color: '#d97706', padding: '8px 10px', textAlign: 'center' }}>{u.confirmada}</td>
                      <td style={{ borderBottom: '1px solid var(--color-border-tertiary)', color: '#6b7280', padding: '8px 10px', textAlign: 'center' }}>{u.realizada}</td>
                      <td style={{ borderBottom: '1px solid var(--color-border-tertiary)', color: '#e12f3b', padding: '8px 10px', textAlign: 'center' }}>{u.cancelada}</td>
                      <td style={{ borderBottom: '1px solid var(--color-border-tertiary)', color: 'var(--color-text-primary)', fontWeight: 700, padding: '8px 10px', textAlign: 'center' }}>{u.total}</td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          </>
        )}

        {/* ── Motivos de cancelación ────────────────────────── */}
        {canceladas > 0 && (
          <>
            <SectionTitle icon="circle-x" title="Motivos de cancelación" />
            <div style={{ display:'grid', gridTemplateColumns:'repeat(auto-fit,minmax(280px,1fr))', gap:'20px' }}>
              <div>
                {porMotivoCancelacion.length===0
                  ? <p style={{fontSize:'13px',color:'var(--color-text-secondary)'}}>Sin motivos capturados en el rango seleccionado</p>
                  : porMotivoCancelacion.map(({label,count})=>(
                      <BarRow key={label} label={label} value={count} max={maxMotivoCancelacion} color="#e12f3b"/>
                    ))
                }
                {sinMotivoCancelacion > 0 && (
                  <p style={{ fontSize:'11px', color:'var(--color-text-secondary)', marginTop:'6px' }}>
                    {sinMotivoCancelacion} cancelación{sinMotivoCancelacion===1?'':'es'} sin motivo capturado (registradas antes de que el motivo fuera obligatorio)
                  </p>
                )}
              </div>
              <div>
                <DonaChart segments={donaMotivoCancelacion}/>
              </div>
            </div>
          </>
        )}

        {/* ── Motivos de reagendamiento ────────────────────── */}
        {reagendados > 0 && (
          <>
            <SectionTitle icon="calendar-repeat" title="Motivos de reagendamiento" />
            <div style={{ display:'grid', gridTemplateColumns:'repeat(auto-fit,minmax(280px,1fr))', gap:'20px' }}>
              <div>
                {porMotivoReagendamiento.length===0
                  ? <p style={{fontSize:'13px',color:'var(--color-text-secondary)'}}>Sin motivos disponibles en el rango seleccionado</p>
                  : porMotivoReagendamiento.map(({label,count})=>(
                      <BarRow key={label} label={label} value={count} max={maxMotivoReagendamiento} color="#0582a9"/>
                    ))
                }
              </div>
              <div>
                <DonaChart segments={donaMotivoReagendamiento}/>
              </div>
            </div>
          </>
        )}

        {/* ── Tendencia diaria ─────────────────────────────── */}
        <SectionTitle icon="calendar-stats" title="Cirugías por día" />
        <TendenciaDiaria docs={docs} desde={rangoDesde} hasta={rangoHasta}/>

        {/* ── Ranking con tabs ─────────────────────────────── */}
        <SectionTitle icon="trophy" title="Rankings" />
        <div style={{ display:'flex', gap:'4px', flexWrap:'wrap', marginBottom:'16px' }}>
          {TABS.map(t=>(
            <button key={t.id} onClick={()=>setTab(t.id)} style={{
              background  : tab===t.id ? TEAL : 'transparent',
              border      : `0.5px solid ${tab===t.id ? TEAL : 'var(--color-border-secondary)'}`,
              borderRadius: 'var(--border-radius-md)',
              color       : tab===t.id ? '#fff' : 'var(--color-text-secondary)',
              cursor      : 'pointer',
              fontSize    : '12px',
              fontWeight  : tab===t.id ? 500 : 400,
              padding     : '5px 12px',
            }}>
              {t.label}
            </button>
          ))}
        </div>
        {(rankingData[tab]||[]).length===0
          ? <p style={{fontSize:'13px',color:'var(--color-text-secondary)'}}>Sin datos disponibles</p>
          : (rankingData[tab]||[]).map(({label,count})=>(
              <BarRow key={label} label={label} value={count} max={maxRanking} color={TEAL}/>
            ))
        }

        {/* ── Horas ocupadas por sala ──────────────────────── */}
        <SectionTitle icon="clock-hour-4" title="Horas quirúrgicas por sala" />
        {horasPorSala.map(({label,count})=>(
          <BarRow key={label} label={label} value={count} displayValue={fmtH(count)} max={maxHoras} color='#0582a9'/>
        ))}

        {/* ── Requerimientos especiales ────────────────────── */}
        <SectionTitle icon="microscope" title="Requerimientos especiales" />
        <div style={{ display:'grid', gridTemplateColumns:'repeat(auto-fit,minmax(140px,1fr))', gap:'12px' }}>
          {/* Imagenología — oculto por el momento a petición (el campo del
              formulario que la alimentaba también está oculto). Para
              reactivar, descomentar esta tarjeta. */}
          {/* <KpiCard icon={<i className="ti ti-radio" aria-hidden="true"/>}        label="Imagenología"      value={reqImg}  color='#0582a9'/> */}
          <KpiCard icon={<i className="ti ti-tools" aria-hidden="true"/>}        label="Material especial" value={reqMat}  color='#d97706'/>
          <KpiCard icon={<i className="ti ti-device-laptop" aria-hidden="true"/>}label="Renta de equipos"  value={reqRent} color='#85c100'/>
          <KpiCard icon={<i className="ti ti-flask" aria-hidden="true"/>}        label="Muestra patológica"value={reqMue}  color='#e12f3b'/>
        </div>

      </>}
    </div>
  );
});

export default DashboardPage;